import { useContext, useState } from 'react';
import { useQuery } from 'react-query';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import useAxios from './useAxios';
import useSubscriber from './useSubscriber';

export type LovenseCommandType =
    | 'Vibrate'
    | 'Rotate'
    | 'Pump'
    | 'Thrusting'
    | 'Fingering'
    | 'Suction'
    | 'Depth'
    | 'Stroke'
    | 'Oscillate'
    | 'Stop';

interface CommandRange {
    min: number;
    max: number;
}

export const LOVENSE_TOY_RANGES: Record<string, CommandRange> = {
    Vibrate: { min: 0, max: 20 },
    Rotate: { min: 0, max: 20 },
    Pump: { min: 0, max: 3 },
    Thrusting: { min: 0, max: 20 },
    Fingering: { min: 0, max: 20 },
    Suction: { min: 0, max: 20 },
    Depth: { min: 0, max: 3 },
    Stroke: { min: 0, max: 100 },
    Oscillate: { min: 0, max: 20 },
};

// Map partial toy names to their capabilities
export const LOVENSE_TOY_CAPABILITIES: Record<string, LovenseCommandType[]> = {
    lush: ['Vibrate'],
    hush: ['Vibrate'],
    domi: ['Vibrate'],
    ambi: ['Vibrate'],
    dolce: ['Vibrate'],
    calor: ['Vibrate'],
    ferri: ['Vibrate'],
    hyphy: ['Vibrate'],
    edge: ['Vibrate'],
    nora: ['Vibrate', 'Rotate'],
    max: ['Vibrate', 'Pump'],
    osci: ['Vibrate', 'Oscillate'],
    gush: ['Vibrate', 'Oscillate'],
    gravity: ['Thrusting', 'Rotate'],
    spinel: ['Vibrate', 'Thrusting'],
    flexer: ['Vibrate', 'Fingering'],
    tenera: ['Vibrate', 'Suction'],
    solace: ['Stroke'],
};

export interface ToyAction {
    feature: LovenseCommandType;
    strength: number; // 0-20
}

export interface TipRule {
    id: string;
    tipAmount: number;
    duration: number; // in seconds
    actions: ToyAction[]; // Array allows combination (e.g., Vibrate + Rotate)
}

export interface LovenseToyItem {
    onlyfansId: number;
    toyId: string;
    nickName: string;
    name: string;
    status: number;
}

export type lovenseDurationMode = 'dynamic' | 'fixed';

export type lovensePatterns = 'nothing';

export interface LovenseSettingItem {
    onlyfansId: number;
    settingId?: string;
    toyId: string;
    durationPerDollar: number;
    intensity: number;
    startDollarAmount: number;
    endingDollarAmount: number;
    durationMode?: lovenseDurationMode;
    pattern?: lovensePatterns;
}

const useLovense = (autoRefetch = false) => {
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const { data: subscriber } = useSubscriber();
    const axios = useAxios();

    const [isConnectingToy, setIsConnectingToy] = useState<boolean>(false);
    const [isServiceStatusLoading, setIsServiceStatusLoading] = useState<boolean>(false);
    const [isSendingTestCommand, setIsSendingTestCommand] = useState<boolean>(false);
    const [isToySettingsUpdating, setIsToySettingsUpdating] = useState<boolean>(false);
    const [isToyDeleting, setIsToyDeleting] = useState<boolean>(false);

    // Fetch Subscriber's Mass DM
    const fetchData = async (): Promise<LovenseToyItem[] | null> => {
        const query: string = `${settingsContext.routes.lovense.base}${subscriber._id}/toys`;

        return axios
            .get(query)
            .then(response => response.data)
            .catch(() => {
                return null;
            });
    };

    const lovenseToys = useQuery(['devicesLovenseToys', subscriber?._id], () => fetchData(), {
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchInterval: autoRefetch ? 5 * 1000 : false, // 5 seconds
        enabled: userContext.jwtToken && settingsContext.apiKey && subscriber && subscriber._id ? true : false,
    });

    // Get service active status
    const getLovenseServiceActive = async (): Promise<boolean> => {
        const query: string = `${settingsContext.routes.lovense.base}${subscriber._id}/active`;

        setIsServiceStatusLoading(true);

        return axios
            .get(query)
            .then(response => {
                setIsServiceStatusLoading(false);

                return response.data.active === true;
            })
            .catch(() => {
                setIsServiceStatusLoading(false);

                return false;
            });
    };

    // Set service active status
    const setLovenseServiceActive = async (active: boolean): Promise<boolean> => {
        const query: string = `${settingsContext.routes.lovense.base}${subscriber._id}/active`;

        setIsServiceStatusLoading(true);

        return axios
            .post(query, { active })
            .then(response => {
                setIsServiceStatusLoading(false);

                return response.data.success === true;
            })
            .catch(() => {
                setIsServiceStatusLoading(false);

                return false;
            });
    };

    // Delete a connected toy
    const deleteLovenseToy = async (toyId: string): Promise<boolean> => {
        const query: string = `${settingsContext.routes.lovense.base}${subscriber._id}/toys/${toyId}`;

        setIsToyDeleting(true);

        return axios
            .delete(query)
            .then(response => {
                setIsToyDeleting(false);

                return response.data.success === true;
            })
            .catch(() => {
                setIsToyDeleting(false);

                return false;
            });
    };

    // Get QR Code to connect a new Lovense toy
    const getLovenseQrCode = async (): Promise<{ qrUrl: string; code: string } | null> => {
        const query: string = `${settingsContext.routes.lovense.base}${subscriber._id}/qr`;

        setIsConnectingToy(true);

        return axios
            .get(query)
            .then(response => {
                setIsConnectingToy(false);

                if (response.status !== 200) {
                    console.error('Error fetching Lovense QR code:', response.data);

                    return null;
                }

                return response.data;
            })
            .catch(error => {
                setIsConnectingToy(false);

                console.error('Error fetching Lovense QR code:', error);

                return null;
            });
    };

    // Send test command to toy
    const sendTestCommandToToy = async (toyId: string): Promise<boolean> => {
        const query: string = `${settingsContext.routes.lovense.base}${subscriber._id}/test/${toyId}`;

        setIsSendingTestCommand(true);

        return axios
            .post(query)
            .then(response => {
                setIsSendingTestCommand(false);

                return response.data.success === true;
            })
            .catch(error => {
                setIsSendingTestCommand(false);

                console.error('Error sending test command to toy:', error);

                throw error;
            });
    };

    // Update Lovense Toy Tip Settings
    const updateLovenseToySettings = async (toyId: string, tipSettings: LovenseSettingItem): Promise<boolean> => {
        const query: string = `${settingsContext.routes.lovense.base}${subscriber._id}/toys/${toyId}/settings`;

        setIsToySettingsUpdating(true);

        return axios
            .post(query, tipSettings)
            .then(response => {
                setIsToySettingsUpdating(false);

                return response.data.success === true;
            })
            .catch(() => {
                setIsToySettingsUpdating(false);

                return false;
            });
    };

    return {
        ...lovenseToys,
        getLovenseQrCode,
        isConnectingToy,
        getLovenseServiceActive,
        setLovenseServiceActive,
        isServiceStatusLoading,
        sendTestCommandToToy,
        isSendingTestCommand,
        updateLovenseToySettings,
        isToySettingsUpdating,
        deleteLovenseToy,
        isToyDeleting,
    };
};

export default useLovense;
